import { FC, FormEvent, useEffect, useRef } from 'react';

import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
// import { AutoPlayView } from '@pp/core/src/components/game/autoPlay/AutoPlay';
import { GameButtonProps } from '@pp/core/src/components/game/gameButton/gameButton';
import { Logger } from '@pp/core/src/logger/Logger';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { BetType } from '@pp/core/src/store/slices/bets/types';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';

import { Bets } from '../../config/bets';
import { useAutoCashoutSlice } from '../../store/slices/autocashout/selectors';
import { useCrashAutoplaySlice } from '../../store/slices/crashAutoplay/selectors';
import { crashAutoplayActions } from '../../store/slices/crashAutoplay/slice';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';
import { useIsBetConfirmedConnected } from '../gameGrid/hooks/useIsBetConfirmedConnected';
import { useOnBetCallbackConnected } from '../gameGrid/hooks/useOnBetCallbackConnected';
import { useOnCancelCallbackConnected } from '../gameGrid/hooks/useOnCancelCallbackConnected';
import { useValidBetInputValue } from '../gameGrid/hooks/useValidBetInputValue';

import { AutoPlayView } from './AutoPlay';
import { CrashAutoPlayLimitRef, CrashAutoPlayLimitInputsConnected } from './CrashAutoplayLimitsConnected';

export interface CrashAutoplayModalConnectedProps extends Omit<GameButtonProps, 'icon'> {
  betId: Bets;
}
export const CrashAutoplayModalConnected: FC<CrashAutoplayModalConnectedProps> = ({ betId }) => {
  const dispatch = useCoreDispatch();
  const currentGameId = useGameStateSlice.gameId();
  const amount = useCrashAutoplaySlice.getRemainingCount(betId);
  const limitsRef = useRef<CrashAutoPlayLimitRef>(null);
  const currentBet = useBetSlice.bets();
  const earlybets = useHighflyerStateSlice.getEarlyBetAmount(betId);
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const isInProgress = useCrashAutoplaySlice.isInProgress(betId);
  const { betInputAmount } = useValidBetInputValue(betId);
  const isOpen = useCrashAutoplaySlice.isOpen(betId);
  const onBet = useOnBetCallbackConnected();
  const isBetConfirmed = useIsBetConfirmedConnected(betId);
  const isInfitniteAutoPlay = useConfigSlice.isInfiniteAutoPlayEnabled();
  const onCancel = useOnCancelCallbackConnected(betId);
  const isInDealingInProgress = useHighflyerStateSlice.isDealingInProgress(betId);

  const { isEnabled, cashoutMultiplier } = useAutoCashoutSlice.getAutoCashoutObj(betId);
  useEffect(() => {
    if (isEnabled || betInputAmount) {
      dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: Number(betId), thisRoundonly: false }));
    }
  }, [isEnabled, cashoutMultiplier, betInputAmount, dispatch, betId]);

  const enableInfiniteAutoplay = useCrashAutoplaySlice.getInfiniteAutoplayEnabled(betId);

  const toggleOpen = (): void => {
    dispatch(crashAutoplayActions.toggleOpen(betId));
    Logger.logEvent('click', 'autoplay', Date.now(), 1, '', false);
  };
  const onStop = (): void => {
    if (!isBetsOpen && isInDealingInProgress) onCancel(betId);
    dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
    toggleOpen();
    Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, 'Manual', false);
    dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
  };
  const launchAutoplay = (event?: FormEvent<HTMLFormElement>): boolean => {
    event?.preventDefault();
    const validationPassed = enableInfiniteAutoplay ? true : event && limitsRef.current?.onSubmit(event, betId);

    const betAmount = currentBet
      ? currentBet.betSpots[betId].value > 0
        ? currentBet.betSpots[betId].value
        : earlybets
      : 0;
    if (!validationPassed) {
      return false;
    }
    if (isInProgress) {
      if (betAmount <= 0) return false;
    }
    if (!isInDealingInProgress) dispatch(betActions.clearBet(betId)); // only single bet should set accordingly the auto play kept bet id
    onBet(betId, betInputAmount);
    if (isBetConfirmed && currentBet) {
      const newBet: BetType = { betSpots: {} };
      newBet.betSpots[betId] = currentBet.betSpots[betId].value > 0 ? currentBet.betSpots[betId] : { value: earlybets };
      dispatch(
        crashAutoplayActions.saveBet({
          bestSpot: betId,
          savedBet: { betType: newBet, isEnable: isEnabled, value: cashoutMultiplier },
          gameId: currentGameId,
        })
      );
    } else {
      dispatch(
        crashAutoplayActions.saveBet({
          bestSpot: betId,
          savedBet: {
            betType: {
              betSpots: {
                [betId]: {
                  value: betInputAmount,
                },
              },
            },
            isEnable: isEnabled,
            value: cashoutMultiplier,
          },
          gameId: currentGameId,
        })
      );
    }
    if (enableInfiniteAutoplay) {
      dispatch(crashAutoplayActions.updateRoundCount({ bestSpot: betId, count: parseInt('-1', 10) }));
    } else {
      const { amountValue } = event?.target as HTMLFormElement;
      dispatch(crashAutoplayActions.updateRoundCount({ bestSpot: betId, count: parseInt(amountValue.value, 10) }));
      toggleOpen();
    }

    if (!isInProgress) {
      dispatch(crashAutoplayActions.start({ midRound: !isBetsOpen, bestSpot: betId }));
    } else {
      if (!isInfitniteAutoPlay) {
        dispatch(toastActions.createToast('AUTOPLAY_ROUNDS_MODIFIED'));
      }
    }

    return false;
  };

  useEffect(() => {
    if (enableInfiniteAutoplay) {
      dispatch(toastActions.createToast('TOAST_AUTOPLAY_INFINITE'));
      launchAutoplay();
    } else if (enableInfiniteAutoplay == false) {
      // checking == false becasue !null even runing the condition
      dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
      dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
      dispatch(crashAutoplayActions.setInfiniteAutoplayEnabledOnCalncel({ bestSpot: betId, cancelled: null }));
      onCancel(betId);
    }
  }, [enableInfiniteAutoplay, dispatch]);

  return (
    <AutoPlayView
      totalBetNode={<CurrencyConnected value={betInputAmount} />}
      isInProgress={isInProgress}
      open={isOpen}
      roundsLeft={amount}
      onSubmit={launchAutoplay}
      onStop={onStop}
      toggleOpen={toggleOpen}
      startEnabled={betInputAmount > 0}
      totalBet={betInputAmount}
      limitNode={<CrashAutoPlayLimitInputsConnected ref={limitsRef} betId={betId} />}
      betId={betId}
    />
  );
};
